import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@material-ui/core/Grid';
import { FormHelperText, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, withStyles } from '@material-ui/core';

import moment from 'moment';
import { SecondaryButton, SingleSelect, DatePicker, formatters } from '../../../../Core/Controls';
import { useToast } from '../../../../Core/Hooks/useToast';
import { getVehicleJobs_Reallocate } from '../../../../Core/Services/InternalUser/ServiceAgent/breakdownService';
import { getAllGridScheduleDropdowns } from '../../../../Core/Services/InternalUser/ControllerService/scheduleVehicleService';
import { getJobEnggById } from '../../../../Core/Services/InternalUser/ControllerService/jobDetailsService';
import { AppStorage } from '../../../../Core/Basic/storage-service';

const StyledTableCell = withStyles((theme) => ({
    head: {
        // padding: ' 5px',
        fontSize: 15,
        fontWeight: 600,
        backgroundColor: '#bdbdbd82'
    },
    body: {
        fontSize: 12
        // padding: ' 5px'
        // fontSize: 11,
    }
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:hover': {
            backgroundColor: 'grey',
            color: 'white'
        },
        cursor: 'pointer'
    }
}))(TableRow);

const JobAllocationModal = (props) => {
    const [state, setState] = useState({
        errors: {},
        contractDeliveryDate: '', //moment().format('YYYY-MM-DD')
        contractDeliveryCharge: null,
        serviceAgent: null,
        serviceAgents: [],
        technician: [],
        filterTechnician: [],
        dueDate: '' // single top-level due date applied to all selected records, gated on permission
    });
    const [btnLoader, setBtnLoader] = useState(false);
    const { showToastSuccess, showToastError } = useToast();

    // Show the Due Date field only for users with the mass-allocation due-date permission.
    const checkhaveAccess = AppStorage.getAllowAcces();
    const canSetDueDate = !!checkhaveAccess?.CanSetMassAllocationDueDate;

    const PullEngineerList = useCallback(async (serviceAreaID) => {
        if (serviceAreaID) {
            let res = await getJobEnggById(serviceAreaID);
            if (res.success) {
                setState((d) => ({
                    ...d,
                    filterTechnician: res?.data?.list || [],
                    technician: res?.data?.list || []
                }));
            }
        } else {
            setState((d) => ({
                ...d,
                filterTechnician: [],
                technician: []
            }));
        }
    }, []);

    // Service Area dropdown sourced from Schedules/ScheduleAddEditDropdowns
    // (no VehicleID) — its response includes serviceAgents.
    useEffect(() => {
        const fetchDropdowns = async () => {
            let res = await getAllGridScheduleDropdowns();
            setState((st) => ({
                ...st,
                serviceAgents: res?.data?.serviceAgents || []
            }));
        };
        fetchDropdowns();
    }, []);

    const onFieldChange = (e) => {
        const { name, value } = e.target;
        setState((st) => ({ ...st, [name]: value }));
    };

    const singleSelectChange = (nm) => (ev, val) => {
        if (nm === 'serviceAgent') {
            setState((st) => ({
                ...st,
                serviceAgent: val,
                engineerID: null,
                filterTechnician: [],
                technician: [],
                errors: { ...st.errors, serviceAgent: '' }
            }));
            if (val?.id) {
                PullEngineerList(val.id);
            }
            return;
        }
        setState((st) => ({ ...st, [nm]: val }));
    };

    const validations = () => {
        const { engineerID, serviceAgent } = state;
        let formIsValid = true;
        let errors = {};

        // if (!engineerID?.id) {
        //     errors.engineerID = 'This field is required';
        //     formIsValid = false;
        // }

        if (!serviceAgent?.id) {
            errors.serviceAgent = 'This field is required';
            formIsValid = false;
        }
        setState((st) => ({ ...st, errors: errors }));
        return formIsValid;
    };

    const SubmitHandler = async () => {
        if (validations()) {
            let data = {
                jobIds: props.selectedRecords.map((q) => q.jobID),
                engineerID: state.engineerID?.id,
                serviceAgent: state.serviceAgent?.id
            };
            // Single top-level due date applied to all selected records, only when the user
            // has the permission. NOTE: confirm the exact payload field name the backend expects.
            if (canSetDueDate && state.dueDate) {
                data.dueDate = state.dueDate;
            }
            // console.log(data, 'data');
            try {
                setBtnLoader(true);
                let res = await getVehicleJobs_Reallocate(data);
                if (res.success) {
                    showToastSuccess(`${res.message}`);
                    setBtnLoader(false);
                    props.onClose(true);
                } else {
                    showToastError(res.message);
                    setBtnLoader(false);
                }
            } catch (error) {
                setBtnLoader(false);
                showToastError(`An error occurred: ${error.message}`);
            }
        }
    };
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} className="mandatory-fields">
                <InputLabel shrink>Service Area</InputLabel>
                <SingleSelect options={state.serviceAgents} value={state.serviceAgent} onChange={singleSelectChange('serviceAgent')} />
                <FormHelperText error>{state.errors.serviceAgent}</FormHelperText>
            </Grid>
            <Grid item xs={12} sm={6}>
                <InputLabel shrink>Engineer</InputLabel>
                <SingleSelect options={state.filterTechnician || state.technician || []} value={state.engineerID} onChange={singleSelectChange('engineerID')} />
                <FormHelperText error>{state.errors.engineerID}</FormHelperText>
            </Grid>
            {canSetDueDate && (
                <Grid item xs={12} style={{ marginBottom: 12 }}>
                    <DatePicker inputLabel="Due Date" value={state.dueDate} name="dueDate" onChange={onFieldChange} />
                    <FormHelperText
                        error
                        component="div"
                        style={{ marginTop: 6, fontWeight: 600, backgroundColor: '#fdecea', padding: '8px 12px', borderRadius: 4 }}
                    >
                        Setting the due date will change the booking and due date of selected jobs/events. Don't set the due date if you don't want to update
                        the dates.
                    </FormHelperText>
                </Grid>
            )}
            <Grid item xs={12}>
                <TableContainer className="custom-scroll" style={{ height: '350px' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow className="header">
                                <StyledTableCell>Reg Number</StyledTableCell>
                                <StyledTableCell>Booking Date</StyledTableCell>
                                <StyledTableCell>Job Description</StyledTableCell>
                                <StyledTableCell>Customer</StyledTableCell>
                                <StyledTableCell>Service Area</StyledTableCell>
                                <StyledTableCell>Engineer</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.selectedRecords.map((q) => {
                                return (
                                    <StyledTableRow key={q.vehicleRegNumber}>
                                        <StyledTableCell>{q.vehicleRegNumber}</StyledTableCell>
                                        <StyledTableCell>{formatters.DateTimeFormatter(q.bookingDate)}</StyledTableCell>
                                        <StyledTableCell>{q.jobDescription}</StyledTableCell>
                                        <StyledTableCell>{q.customerCompanyName}</StyledTableCell>
                                        <StyledTableCell>{q.serviceAgentName}</StyledTableCell>
                                        <StyledTableCell>{q.technician}</StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            <Grid item xs={12}>
                <SecondaryButton fullWidth isBtnLoader={btnLoader} className="submit_btn" onClick={SubmitHandler} disabled={props.selectedRecords.length <= 0}>
                    Submit
                </SecondaryButton>
            </Grid>
        </Grid>
    );
};

export default JobAllocationModal;
